import type { AvailableMailingProductsDto } from '@/services/ws-post/src/main/resources/rest-api';
import { MailingProduct, MailingType } from '@/services/ws-post/src/main/resources/rest-api';

import { filterHolidayDays, isWeekday } from './helpers.ts';

export const DEFAULT_MAILING_PRODUCTS_WITHOUT_FLAP: MailingProduct[] = Object.values(MailingProduct).filter(
  (product) => product !== MailingProduct.FLAP,
);

export const isFlapProduct = (product: MailingProduct | undefined): boolean => product === MailingProduct.FLAP;

export const shouldDisableOfficialMailingType = (product: MailingProduct | undefined): boolean => isFlapProduct(product);

export const getProductsForMailingType = (
  available: AvailableMailingProductsDto | undefined,
  mailingType: MailingType | undefined,
): MailingProduct[] => {
  if (!available) {
    return DEFAULT_MAILING_PRODUCTS_WITHOUT_FLAP;
  }

  if (mailingType === MailingType.OFFICIAL) {
    return available.officialProducts;
  }

  return available.commercialProducts;
};

export const REVISED_DELIVERY_DAYS_FROM_YEAR = 2027;

export const isFlapDistributionDateAllowed = (date: Date, isRevisedPromopostDeliveryDaysEnabled = false): boolean => {
  const day = date.getDay();
  const isRevisedDeliveryDate = isRevisedPromopostDeliveryDaysEnabled && date.getFullYear() >= REVISED_DELIVERY_DAYS_FROM_YEAR;

  if (isRevisedDeliveryDate) {
    return day === 3;
  }

  return day === 2 || day === 3;
};

const isRevisedNonFlapDistributionDateAllowed = (date: Date): boolean => {
  const day = date.getDay();

  return day === 3 || day === 5;
};

export const filterDistributionDateForProduct = (
  product: MailingProduct | undefined,
  date: Date,
  isRevisedPromopostDeliveryDaysEnabled = false,
): boolean => {
  if (!filterHolidayDays(date)) {
    return false;
  }

  if (isFlapProduct(product)) {
    return isFlapDistributionDateAllowed(date, isRevisedPromopostDeliveryDaysEnabled);
  }

  const isRevisedDeliveryDate = isRevisedPromopostDeliveryDaysEnabled && date.getFullYear() >= REVISED_DELIVERY_DAYS_FROM_YEAR;

  if (isRevisedDeliveryDate) {
    return isRevisedNonFlapDistributionDateAllowed(date);
  }

  return isWeekday(date);
};

export const isDistributionDateValidForProduct = (
  product: MailingProduct | undefined,
  date: Date,
  isRevisedPromopostDeliveryDaysEnabled = false,
): boolean => filterDistributionDateForProduct(product, date, isRevisedPromopostDeliveryDaysEnabled);
